/**
 * Centralized search params parser definitions for nuqs v2
 *
 * These parsers are shared between server and client code to ensure
 * type-safe, consistent parsing of URL search parameters.
 *
 * Import from 'nuqs/server' for server-side usage (loaders, cache)
 * Import from 'nuqs' for client-side hooks
 */

import {
  createLoader,
  createSearchParamsCache,
  createSerializer,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  parseAsStringLiteral,
} from "nuqs/server";

import {
  CollectionCardSortOptions,
  SortOrderEnum,
} from "modules/collections/details/model/collection";
import { DeckSortOptions } from "modules/decks/model/deck";
import {
  ItemSortOptionsEnum,
  TradeListSortOptions,
} from "modules/tradeLists/model/tradeList";
import { SetSortOptionsEnum } from "modules/cards/model/set";
import { CardSortOptionsEnum, PricePeriodEnum } from "modules/cards/model/card";
import {
  registeredFacetFields,
  type FacetContract,
  type FacetContext,
  type FacetFieldKind,
} from "modules/common/cardFacets";
import { BINDER_SECTIONS } from "modules/binder/model/binder";

function parserForFacetField(kind: FacetFieldKind) {
  switch (kind) {
    case "string-array":
      return parseAsArrayOf(parseAsString).withDefault([]);
    case "integer-array":
      return parseAsArrayOf(parseAsInteger).withDefault([]);
    case "integer":
      return parseAsInteger.withDefault(0);
    case "float":
      return parseAsFloat.withDefault(0);
    case "match-mode":
      return parseAsStringLiteral(["and", "or", "exact"] as const).withDefault(
        "or"
      );
  }
}

function bindRegisteredFacetFields(
  contract: FacetContract,
  context: FacetContext | undefined,
  prefix: string
) {
  const fields = registeredFacetFields(contract, context);
  return {
    parsers: Object.fromEntries(
      fields.map((field) => [field.key, parserForFacetField(field.kind)])
    ),
    urlKeys: Object.fromEntries(
      fields.map((field) => [field.key, `${prefix}_${field.key}`])
    ),
  };
}

/**
 * Deck Parsers
 * Used in: /x/decks, /decks pages
 */
export const deckParsers = {
  query: parseAsString.withDefault(""),
  sort: parseAsStringEnum(Object.values(DeckSortOptions)).withDefault(
    DeckSortOptions.UpdatedAt
  ),
  order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
    SortOrderEnum.Descending
  ),
  formatFilter: parseAsString.withDefault(""),
};

export const deckUrlKeys = {
  query: "deck_q",
  sort: "deck_sort",
  order: "deck_order",
} as const;
export const parseDeckSearchParams = createLoader(deckParsers, {
  urlKeys: deckUrlKeys,
});
export const deckParamsCache = createSearchParamsCache(deckParsers, {
  urlKeys: deckUrlKeys,
});

/**
 * Trade List Parsers
 * Used in: /trade/lists/personal, /trade/lists/[list_id] pages
 */
export const tradeListParsers = {
  name: parseAsString.withDefault(""),
  sort: parseAsStringEnum(Object.values(TradeListSortOptions)).withDefault(
    TradeListSortOptions.UpdatedAt
  ),
  order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
    SortOrderEnum.Descending
  ),
};

export const tradeListUrlKeys = { sort: "tl_sort", order: "tl_order" } as const;
export const parseTradeListSearchParams = createLoader(tradeListParsers, {
  urlKeys: tradeListUrlKeys,
});
export const tradeListParamsCache = createSearchParamsCache(tradeListParsers, {
  urlKeys: tradeListUrlKeys,
});

/**
 * Set Parsers
 * Used in: /sets/[game] page
 */
export const setParsers = {
  name: parseAsString.withDefault(""),
  sort: parseAsStringEnum(Object.values(SetSortOptionsEnum)).withDefault(
    SetSortOptionsEnum.ReleaseDate
  ),
  order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
    SortOrderEnum.Descending
  ),
};

export const setUrlKeys = { sort: "set_sort", order: "set_order" } as const;
export const parseSetSearchParams = createLoader(setParsers, {
  urlKeys: setUrlKeys,
});

export const CityTabValues = ["haves", "wants", "matched"] as const;
export type CityTabValue = (typeof CityTabValues)[number];

// The free-text card filter keys don't depend on the manifest; exported so a
// component that only needs the key doesn't have to bind the whole contract.
export const LGS_CARD_QUERY_URL_KEY = "lgs_q";
export const USER_CARD_QUERY_URL_KEY = "user_q";

// Bind the already-loaded API contract to nuqs. Field names and parser
// selection come from the manifest; browser URL keys are namespaced forms of
// the registered field key, while API serialization uses field.queryKey.
// Only route-local state and URL namespaces are declared here.
export function bindFacetSearchParams(contract: FacetContract) {
  const binderFacet = bindRegisteredFacetFields(contract, undefined, "binder");
  const collectionFacet = bindRegisteredFacetFields(
    contract,
    "collection",
    "col"
  );
  const cardFacet = bindRegisteredFacetFields(contract, "cards", "card");
  const tradeListFacet = bindRegisteredFacetFields(contract, "trade", "tli");
  const lgsFacet = bindRegisteredFacetFields(contract, "trade", "lgs");
  const userFacet = bindRegisteredFacetFields(contract, "trade", "user");
  const cityFacet = bindRegisteredFacetFields(contract, "trade", "city");
  const proposalFacet = bindRegisteredFacetFields(contract, "trade", "pp");

  const collectionParsers = {
    query: parseAsString.withDefault(""),
    sort: parseAsStringEnum(
      Object.values(CollectionCardSortOptions)
    ).withDefault(CollectionCardSortOptions.CreatedAt),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
    type: parseAsStringLiteral([
      "all",
      "traded",
      "not-traded",
    ] as const).withDefault("all"),
    ...collectionFacet.parsers,
  };
  const collectionUrlKeys = {
    query: "col_q",
    sort: "col_sort",
    order: "col_order",
    ...collectionFacet.urlKeys,
    type: "col_type",
  } as const;

  const cardParsers = {
    card: parseAsString.withDefault(""),
    sort: parseAsStringEnum(Object.values(CardSortOptionsEnum)).withDefault(
      CardSortOptionsEnum.Price
    ),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
    format: parseAsString.withDefault(""),
    view: parseAsInteger.withDefault(0),
    pricePeriod: parseAsStringEnum(Object.values(PricePeriodEnum)).withDefault(
      PricePeriodEnum.SevenDays
    ),
    ...cardFacet.parsers,
  };
  const cardUrlKeys = {
    sort: "card_sort",
    order: "card_order",
    ...cardFacet.urlKeys,
  } as const;

  const tradeListItemParsers = {
    query: parseAsString.withDefault(""),
    sort: parseAsStringEnum(Object.values(ItemSortOptionsEnum)).withDefault(
      ItemSortOptionsEnum.UpdatedAt
    ),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
    view: parseAsInteger.withDefault(0),
    ...tradeListFacet.parsers,
  };
  const tradeListItemUrlKeys = {
    query: "tli_q",
    sort: "tli_sort",
    order: "tli_order",
    ...tradeListFacet.urlKeys,
  } as const;

  const lgsCardFilterParsers = {
    query: parseAsString.withDefault(""),
    sort: parseAsStringEnum(Object.values(ItemSortOptionsEnum)).withDefault(
      ItemSortOptionsEnum.UpdatedAt
    ),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
    ...lgsFacet.parsers,
  };
  const lgsCardParsers = {
    ...lgsCardFilterParsers,
    setCode: parseAsString.withDefault(""),
    view: parseAsInteger.withDefault(0),
  };
  const lgsCardUrlKeys = {
    query: LGS_CARD_QUERY_URL_KEY,
    sort: "lgs_sort",
    order: "lgs_order",
    ...lgsFacet.urlKeys,
  } as const;

  const userCardFilterParsers = {
    query: parseAsString.withDefault(""),
    sort: parseAsStringEnum(Object.values(ItemSortOptionsEnum)).withDefault(
      ItemSortOptionsEnum.CreatedAt
    ),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
    ...userFacet.parsers,
  };
  const userCardParsers = {
    ...userCardFilterParsers,
    setCode: parseAsString.withDefault(""),
    view: parseAsInteger.withDefault(0),
  };
  const userCardUrlKeys = {
    query: USER_CARD_QUERY_URL_KEY,
    sort: "user_sort",
    order: "user_order",
    ...userFacet.urlKeys,
  } as const;

  const cityCardFilterParsers = {
    query: parseAsString.withDefault(""),
    sort: parseAsStringEnum(Object.values(ItemSortOptionsEnum)).withDefault(
      ItemSortOptionsEnum.UpdatedAt
    ),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
    ...cityFacet.parsers,
  };
  const cityCardParsers = {
    ...cityCardFilterParsers,
    tab: parseAsStringEnum<CityTabValue>([...CityTabValues]).withDefault(
      "haves"
    ),
  };
  const cityCardUrlKeys = {
    query: "city_q",
    sort: "city_sort",
    order: "city_order",
    ...cityFacet.urlKeys,
  } as const;

  const proposalPickerParsers = {
    tab: parseAsStringLiteral(["give", "get"] as const).withDefault("give"),
    searchQuery: parseAsString.withDefault(""),
    sort: parseAsStringEnum(Object.values(ItemSortOptionsEnum)).withDefault(
      ItemSortOptionsEnum.CreatedAt
    ),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
    ...proposalFacet.parsers,
  };
  const proposalPickerUrlKeys = {
    tab: "pp_tab",
    searchQuery: "pp_q",
    sort: "pp_sort",
    order: "pp_order",
    ...proposalFacet.urlKeys,
  } as const;

  const binderParsers = {
    ...binderFacet.parsers,
    inTrade: parseAsBoolean.withDefault(false),
    section: parseAsStringLiteral(BINDER_SECTIONS).withDefault("haves"),
    listId: parseAsString.withDefault(""),
    query: parseAsString.withDefault(""),
    sort: parseAsStringEnum(Object.values(ItemSortOptionsEnum)).withDefault(
      ItemSortOptionsEnum.UpdatedAt
    ),
    order: parseAsStringEnum(Object.values(SortOrderEnum)).withDefault(
      SortOrderEnum.Descending
    ),
  };
  const binderUrlKeys = {
    ...binderFacet.urlKeys,
    inTrade: "in_trade",
    section: "section",
    listId: "list",
    query: "q",
    sort: "sort",
    order: "order",
  } as const;

  return {
    facetContract: contract,
    collectionParsers,
    collectionUrlKeys,
    parseCollectionSearchParams: createLoader(collectionParsers, {
      urlKeys: collectionUrlKeys,
    }),
    collectionParamsCache: createSearchParamsCache(collectionParsers, {
      urlKeys: collectionUrlKeys,
    }),
    serializeCollectionParams: createSerializer(collectionParsers, {
      urlKeys: collectionUrlKeys,
    }),
    cardParsers,
    cardUrlKeys,
    parseCardSearchParams: createLoader(cardParsers, { urlKeys: cardUrlKeys }),
    tradeListItemParsers,
    tradeListItemUrlKeys,
    parseTradeListItemSearchParams: createLoader(tradeListItemParsers, {
      urlKeys: tradeListItemUrlKeys,
    }),
    tradeListItemParamsCache: createSearchParamsCache(tradeListItemParsers, {
      urlKeys: tradeListItemUrlKeys,
    }),
    serializeTradeListItemParams: createSerializer(tradeListItemParsers, {
      urlKeys: tradeListItemUrlKeys,
    }),
    lgsCardFilterParsers,
    lgsCardParsers,
    lgsCardUrlKeys,
    parseLGSCardSearchParams: createLoader(lgsCardParsers, {
      urlKeys: lgsCardUrlKeys,
    }),
    lgsCardParamsCache: createSearchParamsCache(lgsCardParsers, {
      urlKeys: lgsCardUrlKeys,
    }),
    serializeLGSParams: createSerializer(lgsCardParsers, {
      urlKeys: lgsCardUrlKeys,
    }),
    userCardFilterParsers,
    userCardParsers,
    userCardUrlKeys,
    parseUserCardSearchParams: createLoader(userCardParsers, {
      urlKeys: userCardUrlKeys,
    }),
    userCardParamsCache: createSearchParamsCache(userCardParsers, {
      urlKeys: userCardUrlKeys,
    }),
    cityCardFilterParsers,
    cityCardParsers,
    cityCardUrlKeys,
    parseCityCardSearchParams: createLoader(cityCardParsers, {
      urlKeys: cityCardUrlKeys,
    }),
    cityCardParamsCache: createSearchParamsCache(cityCardParsers, {
      urlKeys: cityCardUrlKeys,
    }),
    serializeCityParams: createSerializer(cityCardParsers, {
      urlKeys: cityCardUrlKeys,
    }),
    proposalPickerParsers,
    proposalPickerUrlKeys,
    parseProposalPickerSearchParams: createLoader(proposalPickerParsers, {
      urlKeys: proposalPickerUrlKeys,
    }),
    serializeProposalPickerParams: createSerializer(proposalPickerParsers, {
      urlKeys: proposalPickerUrlKeys,
    }),
    binderParsers,
    binderUrlKeys,
    parseBinderSearchParams: createLoader(binderParsers, {
      urlKeys: binderUrlKeys,
    }),
    binderParamsCache: createSearchParamsCache(binderParsers, {
      urlKeys: binderUrlKeys,
    }),
    serializeBinderParams: createSerializer(binderParsers, {
      urlKeys: binderUrlKeys,
    }),
  };
}

/**
 * Map / Stores Page Parsers
 * Used in: /stores page for server-side coordinate parsing
 * Mirrors mapSearchParsers from useMapSearchParams.ts (client-side)
 */
export const mapSearchParsers = {
  lat: parseAsFloat,
  lng: parseAsFloat,
  city: parseAsString,
};

export const parseMapSearchParams = createLoader(mapSearchParsers);
export const serializeMapParams = createSerializer(mapSearchParsers);
